import {
    ClauseGuidanceRefreshDivergentError,
    ClauseGuidanceRefreshUnconfirmedError,
    ClauseGuidanceWriteDecision,
    IClauseList,
    IOrganizationClauseGuidance,
    useClauseGuidance,
    useIsClauseGuidancePending,
    useSaveClauseGuidance,
} from "@/api";
import {
    ConfirmationDialog,
    IConfirmationDialogProps,
} from "@/components/shared/dialogs/ConfirmationDialog";
import { LoadingIndicator } from "@/components/shared/LoadingIndicator";

import { Button } from "@/components/shared/buttons/Button";
import { Delete, Edit } from "@mui/icons-material";
import { Box, Link, Stack, Typography } from "@mui/material";
import { useDialogs } from "@toolpad/core";
import { TriangleAlert } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { useRef, useState } from "react";
import { CustomGuidanceForm } from "./CustomGuidanceForm";
import { InheritedGuidanceBlock } from "./InheritedGuidanceBlock";

const TITLE = "Custom Guidance";

const unconfirmedMessage = (write: ClauseGuidanceWriteDecision) =>
    `Custom guidance was ${write.kind === "delete" ? "removed" : "saved"}, but this page could not reload it. Reload the page to confirm.`;

interface IEditSession {
    own: IOrganizationClauseGuidance | null;
    initialValue: string;
}

interface IUnconfirmedWrite {
    write: ClauseGuidanceWriteDecision;
    dataUpdatedAt: number;
}

interface IProps {
    /**
     * The clause to display guidance for
     */
    clause: IClauseList;
}

export const CustomGuidance = ({ clause }: IProps) => (
    <CustomGuidanceContent key={clause.id} clause={clause} />
);

const CustomGuidanceContent = ({ clause }: IProps) => {
    const { open } = useDialogs();
    const [editSession, setEditSession] = useState<IEditSession | null>(null);
    const [unconfirmedWrite, setUnconfirmedWrite] =
        useState<IUnconfirmedWrite | null>(null);
    const [returnFocusToEdit, setReturnFocusToEdit] = useState(false);
    const editButtonRef = useRef<HTMLButtonElement>(null);

    const {
        data: clauseGuidance,
        isPending: isLoading,
        isFetching,
        dataUpdatedAt,
        refetch,
    } = useClauseGuidance(clause.id);
    const { mutateAsync: saveGuidance, isPending: isRemoving } =
        useSaveClauseGuidance(clause.id);
    const isGuidancePending = useIsClauseGuidancePending(clause.id);

    const own = clauseGuidance?.own ?? null;
    const guidance = own?.guidance.trim() ? own.guidance : null;
    const inherited = clauseGuidance?.inherited?.guidance.trim()
        ? clauseGuidance.inherited
        : null;
    const inheritedOwner = inherited?.shared_by_organization_name.trim()
        ? inherited.shared_by_organization_name
        : null;
    const unconfirmedNotice =
        unconfirmedWrite && unconfirmedWrite.dataUpdatedAt === dataUpdatedAt
            ? unconfirmedMessage(unconfirmedWrite.write)
            : null;

    const handleUnconfirmed = (write: ClauseGuidanceWriteDecision) => {
        setEditSession(null);
        setUnconfirmedWrite({ write, dataUpdatedAt });
    };

    const handleEdit = () => {
        setUnconfirmedWrite(null);
        setReturnFocusToEdit(true);
        setEditSession({
            own,
            initialValue: guidance ?? inherited?.guidance ?? clause.guidance,
        });
    };

    const handleCloseEdit = () => {
        setEditSession(null);
    };

    const handleOwnChanged = (
        changedOwn: IOrganizationClauseGuidance | null
    ) => {
        setEditSession((session) => session && { ...session, own: changedOwn });
    };

    const handleWriteConflict = async () => {
        const reloaded = await refetch();
        if (reloaded.isSuccess) {
            handleOwnChanged(reloaded.data.own);
        }
    };

    const removeGuidance = async () => {
        setUnconfirmedWrite(null);
        try {
            await saveGuidance({ kind: "delete" });
            enqueueSnackbar("Custom guidance removed", {
                variant: "success",
            });
        } catch (error) {
            if (error instanceof ClauseGuidanceRefreshUnconfirmedError) {
                handleUnconfirmed(error.confirmedWrite);
                return;
            }
            if (error instanceof ClauseGuidanceRefreshDivergentError) {
                enqueueSnackbar(
                    "Guidance changed after removal. Reload to review it.",
                    {
                        variant: "error",
                    }
                );
                return;
            }
            enqueueSnackbar("Failed to remove custom guidance", {
                variant: "error",
            });
        } finally {
            editButtonRef.current?.focus();
        }
    };

    const handleRemove = () => {
        const payload: IConfirmationDialogProps = {
            title: `Remove Custom Guidance for "${clause.title}"?`,
            description: [
                "Are you sure you want to remove the custom guidance for this clause?",
                inherited &&
                    (inheritedOwner
                        ? `Guidance inherited from ${inheritedOwner} will apply instead.`
                        : "Inherited guidance will apply instead."),
                "This action cannot be undone.",
            ]
                .filter(Boolean)
                .join(" "),
            confirmText: "Remove",
            destructive: true,
            onConfirm: () => {
                void removeGuidance();
            },
        };
        open(ConfirmationDialog, payload);
    };

    if (isLoading) {
        return (
            <Stack gap={2}>
                <Typography fontWeight={500} gutterBottom>
                    {TITLE}
                </Typography>

                <LoadingIndicator size={18} />
            </Stack>
        );
    }

    if (!clauseGuidance) {
        return (
            <Stack gap={2}>
                <Typography fontWeight={500} gutterBottom>
                    {TITLE}
                </Typography>

                <Stack
                    alignItems="center"
                    gap={1}
                    sx={{
                        bgcolor: "#F9FAFB",
                        px: 2.5,
                        py: 1.5,
                        borderRadius: 1,
                    }}
                >
                    <Typography
                        role="alert"
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center" }}
                    >
                        Custom guidance could not be loaded.
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => void refetch()}
                        loading={isFetching}
                    >
                        Try again
                    </Button>
                </Stack>
            </Stack>
        );
    }

    return (
        <Stack gap={2}>
            {editSession ? (
                <>
                    <Typography fontWeight={500} gutterBottom>
                        {TITLE}
                    </Typography>

                    <CustomGuidanceForm
                        clause={clause}
                        own={editSession.own}
                        initialValue={editSession.initialValue}
                        onClose={handleCloseEdit}
                        onUnconfirmed={handleUnconfirmed}
                        onOwnChanged={handleOwnChanged}
                        onWriteConflict={handleWriteConflict}
                    />
                </>
            ) : (
                <>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography fontWeight={500} gutterBottom>
                            {TITLE}
                        </Typography>

                        <Stack direction="row" gap={1}>
                            {guidance && (
                                <Button
                                    variant="text"
                                    size="small"
                                    color="error"
                                    endIcon={<Delete sx={{ ml: -0.25 }} />}
                                    onClick={handleRemove}
                                    loading={isRemoving}
                                    disabled={isGuidancePending}
                                >
                                    Remove
                                </Button>
                            )}

                            <Button
                                ref={editButtonRef}
                                variant="outlined"
                                size="small"
                                endIcon={<Edit />}
                                onClick={handleEdit}
                                autoFocus={returnFocusToEdit}
                                disabled={isGuidancePending}
                            >
                                Edit
                            </Button>
                        </Stack>
                    </Stack>

                    {unconfirmedNotice && (
                        <p
                            role="status"
                            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                        >
                            <TriangleAlert
                                aria-hidden
                                className="mt-0.5 size-4 shrink-0"
                            />
                            {unconfirmedNotice}
                        </p>
                    )}

                    <Box
                        sx={{
                            bgcolor: "#F9FAFB",
                            px: 2.5,
                            py: 1.5,
                            borderRadius: 1,
                        }}
                    >
                        {guidance ? (
                            <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{
                                    textAlign: "left",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                "{guidance}"
                            </Typography>
                        ) : (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ textAlign: "center" }}
                            >
                                Your organization hasn't added any custom
                                guidance for this clause.{" "}
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={handleEdit}
                                    disabled={isGuidancePending}
                                    sx={{
                                        cursor: "pointer",
                                        verticalAlign: "baseline",
                                    }}
                                >
                                    Edit
                                </Link>{" "}
                                this clause to add custom guidance.
                            </Typography>
                        )}
                    </Box>
                </>
            )}

            {inherited && (
                <InheritedGuidanceBlock
                    inherited={inherited}
                    replaced={Boolean(guidance)}
                />
            )}
        </Stack>
    );
};
